/**
 * SolArkSyncPanel - Sol-Ark integration settings panel
 *
 * 2-column layout with separate cards:
 * - Connection Status card (left)
 * - Equipment Summary card (right)
 * - Historical Data Backfill card (full width)
 * - Sync Log card (full width, collapsible)
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Sun,
  Database,
  Play,
  Square,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Calendar,
  Trash2,
  RefreshCw,
  Zap,
  Plug,
  Info,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { authenticatedFetch } from '@/lib/api';
import { formatPreciseAgo } from '@/lib/relativeTime';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { BackfillLogPanel } from './BackfillLogPanel';
import { SolArkSyncHistory } from './SolArkSyncHistory';
import { useSolArkBackfill } from '@/hooks/useSolArkBackfill';
import { useRBAC } from '@/contexts/RBACContext';
import { cn } from '@/lib/utils';

interface SyncStats {
  plants: {
    total: number;
    linked: number;
    lastSync: string | null;
  };
  inverters: {
    total: number;
    linked: number;
    lastSync: string | null;
  };
  units: {
    withSolArk: number;
  };
}

type SyncOperation = 'plants' | 'inverters' | 'telemetry' | null;

type IntervalField = 'telemetry_interval_minutes' | 'full_interval_hours';

const TELEMETRY_INTERVAL_OPTIONS = [5, 10, 15, 30, 60];
const FULL_INTERVAL_OPTIONS = [1, 2, 4, 6, 12, 24];
const DEFAULT_TELEMETRY_INTERVAL_MINUTES = 5;
const DEFAULT_FULL_INTERVAL_HOURS = 2;

export function SolArkSyncPanel() {
  const { isSuperadmin } = useRBAC();
  const {
    logs,
    progress,
    isRunning,
    backfillAllUnits,
    cancelBackfill,
    clearLogs,
  } = useSolArkBackfill();

  // State
  const [startDate, setStartDate] = useState('2024-01-01');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [syncStats, setSyncStats] = useState<SyncStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [syncOperation, setSyncOperation] = useState<SyncOperation>(null);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);
  const [logExpanded, setLogExpanded] = useState(true);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);
  const [isLoadingSyncSetting, setIsLoadingSyncSetting] = useState(true);
  const [isTogglingSyncSetting, setIsTogglingSyncSetting] = useState(false);
  const [telemetryIntervalMinutes, setTelemetryIntervalMinutes] = useState(
    DEFAULT_TELEMETRY_INTERVAL_MINUTES
  );
  const [fullIntervalHours, setFullIntervalHours] = useState(DEFAULT_FULL_INTERVAL_HOURS);
  const [savingIntervalField, setSavingIntervalField] = useState<IntervalField | null>(null);

  // Format timestamp
  const formatTime = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    return formatPreciseAgo(timestamp);
  };

  // Fetch sync stats
  const fetchStats = useCallback(async () => {
    try {
      const [invertersRes, plantsRes, unitsRes] = await Promise.all([
        authenticatedFetch('/api/v1/solark/inverters/linked'),
        authenticatedFetch('/api/v1/solark/plants/linked'),
        authenticatedFetch('/api/v1/admin/units'),
      ]);

      const invertersData = invertersRes.ok ? await invertersRes.json() : { linked: [] };
      const plantsData = plantsRes.ok ? await plantsRes.json() : { plants: [] };
      const unitsData = unitsRes.ok ? await unitsRes.json() : { units: [] };

      // GET /inverters/linked returns { linked: [...] } — rows are inner-joined
      // to units, so every row counts as linked.
      const rawInverters = invertersData.linked || [];
      const plants = plantsData.plants || [];
      const units = unitsData.units || [];

      // Legacy databases can hold duplicate rows sharing an
      // external_inverter_id (the sync tolerates them and logs a warning).
      // Dedupe before summing so a duplicated plant's inverters don't count
      // twice: keep the row with the most recent last_seen_at (missing
      // timestamp loses) — mirrors the backend's pickPrimaryDevice.
      type LinkedInverterRow = {
        external_inverter_id?: string;
        last_seen_at?: string;
        last_status?: { inverter_count?: number } | null;
      };
      const byExternalId = new Map<string, LinkedInverterRow>();
      const noExternalId: LinkedInverterRow[] = [];
      for (const inv of rawInverters as LinkedInverterRow[]) {
        const key = inv.external_inverter_id;
        if (!key) {
          noExternalId.push(inv);
          continue;
        }
        const existing = byExternalId.get(key);
        if (
          !existing ||
          (inv.last_seen_at && (!existing.last_seen_at || inv.last_seen_at > existing.last_seen_at))
        ) {
          byExternalId.set(key, inv);
        }
      }
      const inverters = [...byExternalId.values(), ...noExternalId];

      // Each row is a PLANT; last_status.inverter_count carries its
      // physical-inverter inventory (absent until the first full sync stores
      // it). Fall back to 1 per row so the count degrades to plant count
      // until the inventory lands.
      const physicalInverterCount = (inv: { last_status?: { inverter_count?: number } | null }) => {
        const n = inv.last_status?.inverter_count;
        return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 1;
      };
      const inverterSum = inverters.reduce(
        (sum: number, inv: { last_status?: { inverter_count?: number } | null }) =>
          sum + physicalInverterCount(inv),
        0
      );

      // Get most recent timestamps
      const lastInverterSync = inverters.reduce((latest: string | null, inv: { last_seen_at?: string }) => {
        if (!inv.last_seen_at) return latest;
        return !latest || inv.last_seen_at > latest ? inv.last_seen_at : latest;
      }, null);

      const lastPlantSync = plants.reduce((latest: string | null, plant: { last_seen_at?: string }) => {
        if (!plant.last_seen_at) return latest;
        return !latest || plant.last_seen_at > latest ? plant.last_seen_at : latest;
      }, null);

      setSyncStats({
        plants: {
          total: plants.length,
          linked: plants.filter((p: { unit_id?: string }) => p.unit_id).length,
          lastSync: lastPlantSync,
        },
        inverters: {
          // Physical-inverter sum, NOT plant-row count — "(N linked)" uses the
          // same sum so both numbers stay consistent.
          total: inverterSum,
          linked: inverterSum,
          lastSync: lastInverterSync,
        },
        units: {
          withSolArk: units.length,
        },
      });
    } catch (error) {
      console.error('Failed to fetch sync stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats, syncResult]);

  // Fetch auto-sync setting
  const fetchSyncSetting = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/v1/admin/settings/solark-sync');
      if (response.ok) {
        const data = await response.json();
        setAutoSyncEnabled(data.enabled);
        // Backend is adding these fields — consume defensively with the
        // scheduler's defaults until it ships.
        setTelemetryIntervalMinutes(
          typeof data.telemetry_interval_minutes === 'number'
            ? data.telemetry_interval_minutes
            : DEFAULT_TELEMETRY_INTERVAL_MINUTES
        );
        setFullIntervalHours(
          typeof data.full_interval_hours === 'number'
            ? data.full_interval_hours
            : DEFAULT_FULL_INTERVAL_HOURS
        );
      }
    } catch (error) {
      console.error('Failed to fetch sync setting:', error);
    } finally {
      setIsLoadingSyncSetting(false);
    }
  }, []);

  useEffect(() => {
    fetchSyncSetting();
  }, [fetchSyncSetting]);

  // Toggle auto-sync setting
  const handleToggleAutoSync = async (enabled: boolean) => {
    setIsTogglingSyncSetting(true);
    try {
      const response = await authenticatedFetch('/api/v1/admin/settings/solark-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });

      if (response.ok) {
        setAutoSyncEnabled(enabled);
        setSyncResult({
          success: true,
          message: enabled ? 'Auto-sync enabled' : 'Auto-sync paused',
        });
      } else {
        const data = await response.json();
        setSyncResult({
          success: false,
          message: data.message || 'Failed to update sync setting',
        });
      }
    } catch (error) {
      setSyncResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to update sync setting',
      });
    } finally {
      setIsTogglingSyncSetting(false);
    }
  };

  // Update a single sync-interval field (the route merges partial bodies)
  const handleIntervalChange = async (field: IntervalField, value: number) => {
    setSavingIntervalField(field);
    try {
      const response = await authenticatedFetch('/api/v1/admin/settings/solark-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });

      if (response.ok) {
        if (field === 'telemetry_interval_minutes') {
          setTelemetryIntervalMinutes(value);
        } else {
          setFullIntervalHours(value);
        }
        setSyncResult({
          success: true,
          message:
            field === 'telemetry_interval_minutes'
              ? `Telemetry interval set to ${value} min`
              : `Full sync interval set to ${value} hr`,
        });
      } else {
        const data = await response.json();
        setSyncResult({
          success: false,
          message: data.message || 'Failed to update sync interval',
        });
      }
    } catch (error) {
      setSyncResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to update sync interval',
      });
    } finally {
      setSavingIntervalField(null);
    }
  };

  // Sync handlers
  const handleSync = async (type: SyncOperation) => {
    if (!type) return;

    setSyncOperation(type);
    setSyncResult(null);

    try {
      let endpoint = '';
      switch (type) {
        case 'plants':
          endpoint = '/api/v1/solark/plants/sync';
          break;
        case 'inverters':
          endpoint = '/api/v1/solark/sync';
          break;
        case 'telemetry':
          endpoint = '/api/v1/solark/sync/telemetry';
          break;
      }

      const response = await authenticatedFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autoLink: true }),
      });

      const data = await response.json();
      // /sync and /sync/telemetry nest the service result under `results`;
      // /plants/sync spreads it at the top level.
      const results = data.results ?? data;
      // A globally rate-limited run comes back INSIDE a 200 as
      // { aborted: true, reason: 'auth_rate_limited', success: false, ... } —
      // it must never render as a success.
      const aborted = results?.aborted === true || results?.success === false;

      if (response.ok && !aborted) {
        setSyncResult({
          success: true,
          message: type === 'plants'
            ? `Discovered ${data.discovered || 0} plants, linked ${data.linked || 0}`
            : type === 'inverters'
            ? `Synced ${results?.plants?.synced || 0} plants`
            : 'Telemetry updated',
        });
        fetchStats();
      } else if (aborted && results?.reason === 'auth_rate_limited') {
        setSyncResult({
          success: false,
          message: `Sol-Ark API rate-limited — sync aborted (${results?.plants?.updated ?? 0}/${results?.plants?.total ?? 0} plants updated, ${results?.plants?.skipped ?? 0} skipped)`,
        });
      } else {
        setSyncResult({
          success: false,
          message: results?.error || data.message || `${type} sync failed`,
        });
      }
    } catch (error) {
      setSyncResult({
        success: false,
        message: error instanceof Error ? error.message : 'Sync failed',
      });
    } finally {
      setSyncOperation(null);
    }
  };

  const handleBackfill = async () => {
    await backfillAllUnits({ startDate, endDate });
  };

  const progressPercent = progress.totalUnits > 0
    ? (progress.currentIndex / progress.totalUnits) * 100
    : 0;

  const lastSyncTime = syncStats?.inverters.lastSync || syncStats?.plants.lastSync || null;
  const isConnected = syncStats !== null && (syncStats.plants.total > 0 || syncStats.inverters.total > 0);

  if (!isSuperadmin) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Sync Result Banner */}
      {syncResult && (
        <div className={cn(
          "flex items-center gap-3 p-3 rounded-lg border",
          syncResult.success
            ? "bg-[#8FB560]/10 border-[#8FB560]/30 text-[#8FB560]"
            : "bg-red-500/10 border-red-500/30 text-red-400"
        )}>
          {syncResult.success ? (
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
          )}
          <span className="text-sm">{syncResult.message}</span>
          <button
            onClick={() => setSyncResult(null)}
            className="ml-auto text-current opacity-60 hover:opacity-100"
          >
            ×
          </button>
        </div>
      )}

      {/* Two-Column Grid: Connection + Equipment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Connection Status Card */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-sm font-medium text-white mb-4">Connection Status</h3>

          {/* API Status Row */}
          <div className="p-4 bg-background rounded-lg border border-border flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="bg-[#8FB560]/10 p-2 rounded text-[#8FB560]">
                <Sun className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Sol-Ark API</p>
                <p className="text-xs text-muted-foreground">
                  Last sync: {formatTime(lastSyncTime)}
                </p>
              </div>
            </div>
            {isLoadingStats ? (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                Loading...
              </span>
            ) : isConnected ? (
              <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">
                Connected
              </span>
            ) : (
              <span className="text-xs text-yellow-400 bg-yellow-500/10 px-2 py-1 rounded-full">
                No data
              </span>
            )}
          </div>

          {/* Auto-Sync Toggle + Interval Controls */}
          <div className="mt-3 p-3 bg-background rounded-lg border border-border">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Auto-Sync</span>
                {!autoSyncEnabled && (
                  <span className="text-xs text-yellow-400 bg-yellow-500/10 px-2 py-0.5 rounded">
                    Paused
                  </span>
                )}
              </div>
              <Switch
                checked={autoSyncEnabled}
                onCheckedChange={handleToggleAutoSync}
                disabled={isLoadingSyncSetting || isTogglingSyncSetting}
                className="data-[state=checked]:bg-[#8FB560]"
              />
            </div>

            <div className="mt-3 pt-3 border-t border-border/50 flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Telemetry every</span>
                <select
                  aria-label="Telemetry every"
                  value={telemetryIntervalMinutes}
                  onChange={(e) =>
                    handleIntervalChange('telemetry_interval_minutes', Number(e.target.value))
                  }
                  disabled={isLoadingSyncSetting || savingIntervalField !== null}
                  className="h-7 rounded border border-border bg-transparent px-1.5 text-xs text-foreground disabled:opacity-50"
                >
                  {TELEMETRY_INTERVAL_OPTIONS.map((m) => (
                    <option key={m} value={m}>
                      {m} min
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Full sync every</span>
                <select
                  aria-label="Full sync every"
                  value={fullIntervalHours}
                  onChange={(e) =>
                    handleIntervalChange('full_interval_hours', Number(e.target.value))
                  }
                  disabled={isLoadingSyncSetting || savingIntervalField !== null}
                  className="h-7 rounded border border-border bg-transparent px-1.5 text-xs text-foreground disabled:opacity-50"
                >
                  {FULL_INTERVAL_OPTIONS.map((h) => (
                    <option key={h} value={h}>
                      {h} hr
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Interval takes effect on the next scheduler tick.
            </p>
          </div>

          {/* Sync Actions */}
          <div className="mt-4 pt-4 border-t border-border/50">
            <p className="text-xs text-muted-foreground mb-3">Manual sync actions</p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => handleSync('plants')}
                disabled={syncOperation !== null || isRunning}
                className="bg-[#8FB560] hover:bg-[#7a9d51] gap-2"
              >
                {syncOperation === 'plants' ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Plug className="h-3.5 w-3.5" />
                )}
                Discover Plants
              </Button>
              <Button
                size="sm"
                onClick={() => handleSync('inverters')}
                disabled={syncOperation !== null || isRunning}
                className="bg-[#8FB560] hover:bg-[#7a9d51] gap-2"
              >
                {syncOperation === 'inverters' ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Zap className="h-3.5 w-3.5" />
                )}
                Sync Inverters
              </Button>
              <Button
                size="sm"
                onClick={() => handleSync('telemetry')}
                disabled={syncOperation !== null || isRunning || (syncStats?.inverters.linked ?? 0) === 0}
                className="bg-[#8FB560] hover:bg-[#7a9d51] gap-2"
              >
                {syncOperation === 'telemetry' ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                Update Telemetry
              </Button>
            </div>
          </div>
        </div>

        {/* Equipment Summary Card */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-sm font-medium text-white mb-4">Equipment Summary</h3>

          <div className="space-y-0">
            {/* Plants Row */}
            <div className="flex justify-between items-center py-3 border-b border-border/50">
              <span className="text-sm text-muted-foreground flex items-center gap-2">
                <Plug className="w-3.5 h-3.5" />
                Plants
              </span>
              {isLoadingStats ? (
                <span className="text-sm text-muted-foreground">—</span>
              ) : (
                <span className="text-sm">
                  <span className="text-foreground font-medium tabular-nums">{syncStats?.plants.total ?? 0}</span>
                  <span className="text-muted-foreground ml-1">
                    ({syncStats?.plants.linked ?? 0} linked)
                  </span>
                </span>
              )}
            </div>

            {/* Inverters Row */}
            <div className="flex justify-between items-center py-3 border-b border-border/50">
              <span className="text-sm text-muted-foreground flex items-center gap-2">
                <Zap className="w-3.5 h-3.5" />
                Inverters
              </span>
              {isLoadingStats ? (
                <span className="text-sm text-muted-foreground">—</span>
              ) : (
                <span className="text-sm">
                  <span className="text-foreground font-medium tabular-nums">{syncStats?.inverters.total ?? 0}</span>
                  <span className="text-muted-foreground ml-1">
                    ({syncStats?.inverters.linked ?? 0} linked)
                  </span>
                </span>
              )}
            </div>

            {/* Units Row */}
            <div className="flex justify-between items-center py-3">
              <span className="text-sm text-muted-foreground flex items-center gap-2">
                <Sun className="w-3.5 h-3.5" />
                Units with Sol-Ark
              </span>
              {isLoadingStats ? (
                <span className="text-sm text-muted-foreground">—</span>
              ) : (
                <span className="text-sm text-foreground font-medium tabular-nums">
                  {syncStats?.units.withSolArk ?? 0}
                </span>
              )}
            </div>
          </div>

          {/* Info Footer */}
          <div className="mt-4 pt-4 border-t border-border/50">
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>Telemetry syncs automatically on a schedule</span>
            </div>
          </div>
        </div>
      </div>

      {/* Historical Data Backfill Card (Full Width) */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-sm font-medium text-white mb-4">Historical Data Backfill</h3>

        {/* Date Range + Action - Single row */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 p-3 bg-background rounded-lg border border-border">
            <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isRunning || syncOperation !== null}
                className="bg-transparent border-0 p-0 h-auto text-sm w-[120px] focus-visible:ring-0"
              />
              <span className="text-muted-foreground text-sm">—</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isRunning || syncOperation !== null}
                className="bg-transparent border-0 p-0 h-auto text-sm w-[120px] focus-visible:ring-0"
              />
            </div>
          </div>
          {!isRunning ? (
            <Button
              onClick={handleBackfill}
              disabled={syncOperation !== null || (syncStats?.inverters.linked ?? 0) === 0}
              size="sm"
              className="bg-[#8FB560] hover:bg-[#7a9d51] gap-2"
            >
              <Play className="h-3.5 w-3.5" />
              Start Backfill
            </Button>
          ) : (
            <Button onClick={cancelBackfill} variant="destructive" size="sm" className="gap-2">
              <Square className="h-3.5 w-3.5" />
              Cancel
            </Button>
          )}
        </div>

        {/* Progress Display */}
        {(isRunning || progress.totalUnits > 0) && (
          <div className="mt-4 p-4 bg-background rounded-lg border border-border">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">
                {isRunning ? (
                  <>Processing <span className="font-mono text-foreground">{progress.currentUnit}</span></>
                ) : progress.isCancelled ? 'Cancelled' : 'Complete'}
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {progress.currentIndex} / {progress.totalUnits}
              </span>
            </div>
            <Progress value={progressPercent} className="h-2" />

            {/* Inline Stats */}
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
              <span className="flex items-center gap-1.5 text-[#8FB560]">
                <CheckCircle className="h-3.5 w-3.5" />
                {progress.completedUnits.length} done
              </span>
              <span className="flex items-center gap-1.5 text-yellow-400">
                <AlertTriangle className="h-3.5 w-3.5" />
                {progress.skippedUnits.length} skipped
              </span>
              <span className="flex items-center gap-1.5 text-red-400">
                <AlertCircle className="h-3.5 w-3.5" />
                {progress.failedUnits.length} failed
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Database className="h-3.5 w-3.5" />
                {progress.totalRecordsUpserted.toLocaleString()} records
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Sync Log Card (Full Width, Collapsible) */}
      {logs.length > 0 && (
        <div className="bg-card border border-border rounded-xl">
          <div className="p-4 flex items-center justify-between">
            <button
              onClick={() => setLogExpanded(!logExpanded)}
              className="flex items-center gap-2 text-sm font-medium text-white hover:text-[#8FB560] transition-colors"
            >
              {logExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              Sync Log
              {!logExpanded && (
                <span className="text-xs text-muted-foreground font-normal ml-1">
                  ({logs.length} entries)
                </span>
              )}
            </button>
            {!isRunning && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearLogs}
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Clear
              </Button>
            )}
          </div>
          {logExpanded && (
            <div className="px-4 pb-4">
              <BackfillLogPanel logs={logs} maxHeight="200px" />
            </div>
          )}
        </div>
      )}

      {/* Sync History (Cron Operations Log) */}
      <SolArkSyncHistory defaultExpanded={false} pageSize={20} />
    </div>
  );
}
